import { useCallback, useMemo } from 'react';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { ColumnSettings, Columns, DataGrid } from '@components/data-grid';
import { RowAction } from '@components/data-grid/data-grid-types';
import { EmptyState } from '@components/empty-state';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconEdit } from '@components/icons/edit';
import { IconHired } from '@components/icons/hired';
import { IconJob } from '@components/icons/job';
import { IconTrash } from '@components/icons/trash';
import { IconVisibilityHidden } from '@components/icons/visibility-hidden';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { IconWorkType } from '@components/icons/work-type';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useUpdateWorkTypes, useWorkTypes } from 'api/worktypes/hooks';
import { WorkType } from 'api/worktypes/requests';
import { Header } from 'components/headers';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { usePermissions } from 'features/permissions';

import locale from './locale.json';
import { useWorkTypeDeleteModal } from './work-type-delete-modal';
import { useWorkTypeSidebar } from './work-type-sidebar';

export const WorkTypes = () => {
  const { t } = useScopedTranslate(locale);
  const showSidebar = useWorkTypeSidebar();
  const { hasRolePermission } = usePermissions();

  return (
    <>
      <Header
        // @ts-ignore
        breadcrumbs={<Breadcrumb items={[{ label: t('workTypes') }]} />}
        actions={
          <Button
            text={t('addWorkType')}
            iconLeft={<IconAddPlus />}
            isDisabled={!hasRolePermission('departments:add')}
            tooltip={hasRolePermission('worktypes:add') ? undefined : t('addWorkTypeDisabled')}
            onClick={() => showSidebar()}
          />
        }
      />
      <PageContent noPadding>
        <WorkTypeList />
      </PageContent>
    </>
  );
};

const WorkTypeList = () => {
  const { t } = useScopedTranslate(locale);
  const workTypeQuery = useWorkTypes();

  const actionsForWorkType = useWorkTypeOptions();
  const columns = useMemo<Columns<WorkType & { title: string }>>(
    () => [
      {
        key: 'title',
        label: t('name'),
        render: (worktype) => {
          return (
            <Stack
              space={3}
              orientation="horizontal"
              style={{ opacity: worktype.enabled ? 1 : 0.5 }}
            >
              <IconHired color="purple50" />
              <Text font="regular14" color="purple100" text={worktype.title} />
            </Stack>
          );
        },
      },
      {
        key: 'numJobs',
        labelIcon: IconJob,
        type: 'numeric',
        center: true,
        ratio: 0.1,
        label: t('numJobs'),
      },
    ],
    [t]
  );

  const [settings, setSettings] = useLocalStorageState<
    ColumnSettings<WorkType & { title: string }>
  >({
    key: 'worktypes-columns',
    defaultValue: {
      title: { order: 0, visible: true },
      numJobs: { order: 1, visible: true },
    },
  });

  if (workTypeQuery.isLoading || !workTypeQuery.data) {
    return <Loading />;
  }
  if (workTypeQuery.data.length === 0) {
    return (
      <EmptyState
        icon={<IconWorkType size={80} />}
        header={t('noWorkTypeTitle')}
        content={t('noWorkTypeDescription')}
      />
    );
  }

  return (
    <DataGrid
      label={t('workTypes')}
      data={workTypeQuery.data}
      columnSettings={settings}
      onColumnSettingsChange={setSettings}
      columns={columns}
      actions={actionsForWorkType}
    />
  );
};

function useWorkTypeOptions() {
  const { t } = useScopedTranslate(locale);

  const showSidebar = useWorkTypeSidebar();
  const showDeleteContainer = useWorkTypeDeleteModal();
  const { hasRolePermission } = usePermissions();

  const updateMutation = useUpdateWorkTypes({});

  return useCallback(
    (workType) => {
      const options: Array<RowAction<WorkType>> = [];
      if (hasRolePermission('worktypes:edit')) {
        options.push({
          label: t('edit'),
          icon: <IconEdit />,
          onSelect: () => showSidebar({ workType }),
        });
      }
      if (hasRolePermission('worktypes:edit')) {
        options.push({
          label: workType.enabled ? t('disable') : t('enable'),
          icon: workType.enabled ? <IconVisibilityHidden /> : <IconVisibilityShown />,
          onSelect: () =>
            updateMutation.mutate({
              id: workType.id,
              translations: workType.translations,
              enabled: !workType.enabled,
            }),
        });
      }
      if (hasRolePermission('worktypes:delete')) {
        options.push({
          label: t('remove'),
          icon: <IconTrash />,
          variant: 'danger',
          onSelect: () => showDeleteContainer({ workType }),
        });
      }
      return options;
    },
    [hasRolePermission, t, showSidebar, showDeleteContainer, updateMutation]
  );
}
